import { useUser } from "@features/user"
import Button from "components/Button"
import Loading from "components/Loading"
import useEnv from "hooks/useEnv"
import useOffer from "hooks/useOffer"
import useToast from "hooks/useToast"
import type React from "react"
import { useRef } from "react"
import { isFirefox } from "react-device-detect"
import { Trans, useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { useReactToPrint } from "react-to-print"

import OffersGea from "./components/OffersGea"
import OffersGeabrasil from "./components/OffersGeabrasil"
import OffersGecontur from "./components/OffersGecontur"

const OffersPdf: React.FC = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const componentRef = useRef<HTMLDivElement>(null)

  const { data } = useUser()
  const { isGea, isGeaBrasil, isGecontur } = useEnv()
  const { offerId } = useParams<Record<string, string | undefined>>()
  const { isLoading, offer } = useOffer(offerId)

  const print = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `post-${offerId}`,
  })

  const handlePrint = () => {
    if (isFirefox) {
      toast("warning", t("offersPdf.warning.firefox"))
    }
    print()
  }

  if (isLoading) {
    return (
      <div className="flex mt-40 justify-center">
        <Loading className="" />
      </div>
    )
  }

  if (!offer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="mt-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            <Trans i18nKey="offersPdf.notFound.title" />
          </h3>
          <p className="text-gray-600 mb-4">
            <Trans i18nKey="offersPdf.notFound.description" />
          </p>
          <Button primary medium onClick={() => window.history.back()}>
            <Trans i18nKey="offersPdf.actions.back" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="px-2 py-4 sm:px-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            <Trans i18nKey="offersPdf.title" />
          </h3>
        </div>

        <div className="border-t border-gray-200">
          {isGea && (
            <OffersGea ref={componentRef} offer={offer} agency={data?.agency} />
          )}
          {isGeaBrasil && (
            <OffersGeabrasil
              ref={componentRef}
              offer={offer}
              agency={data?.agency}
            />
          )}
          {isGecontur && (
            <OffersGecontur
              ref={componentRef}
              offer={offer}
              agency={data?.agency}
            />
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-center">
        <Button primary medium onClick={handlePrint}>
          <Trans i18nKey="offersPdf.actions.downloadPdf" />
        </Button>
      </div>
    </>
  )
}

export default OffersPdf
